/**
 * TAG PLACEMENT (2026-10-14, Matt: "I want to be able to custom place any tag in plan view
 * that comes in automatically, like door and window tags, too. That way I can fine tune the
 * drawing if the system didn't work out every overlap").
 *
 * Every auto-placed plan tag family stamps a `tagKey` on the glyphs of one tag
 * (`open:<id>` door/window/niche, `room:<id>`, `anno.equiptag:<id>`, `anno.rcp:<id>` light and
 * ceiling-height tags, `anno.assemblytag:<id>`). Dragging a tag on a plan stores a hand
 * offset here, relative to where the layout put it — so the tag still follows its element when
 * the element moves, and the nudge rides along.
 *
 * Offsets are per PLAN SCOPE (the level id, or the plan type for level-free site/roof plans):
 * every plan of a level shares them, and its sheets and PDFs print them. Two families keep
 * their older homes: wall tags (wallTagOverrides.js, which also holds their hide list) and a
 * room's label on its own level (RoomRecord.labelOffset).
 *
 * Settings key `tagOffsets`: { [scope]: { [tagKey]: [dx, dy] } } — feet.
 */
import { maps, transact, listRooms } from '@aj/bim-kernel'
import { getWallTagOverrides } from './wallTagOverrides.js'
import { edgeToward } from '@aj/bim-geometry/siteOutdoor'

const SETTINGS_KEY = 'tagOffsets'

/**
 * TAG ORIGINS + ADJUSTMENT LOG (2026-10-15): the version of the automatic tag layouts every
 * logged hand move is stamped with. BUMP THIS whenever any auto tag family's placement changes
 * (layoutTags, layoutWallTags, room label anchors, equipment/RCP/assembly tag placement, demo
 * keynotes) — the adjustment metrics compare versions to tell whether a change cut the hand work.
 */
// 3 (2026-10-20): site plan height-marker labels and area-use labels step clear of each other
export const TAG_LAYOUT_VERSION = 3

/** the LEVEL scope (2026-10-14 first cut — every plan of a level shared it; now only the base
 *  that older placements live in, read under each view's own placement) */
export const tagScopeKey = (levelId, planType) => levelId || (planType ? `plan:${planType}` : null)

/**
 * PER-VIEW PLACEMENT (2026-10-14, Matt: "When I move a room tag on the RCP, it moves on all the
 * other plans. The only reason I would move a room tag is to clear it from something on that
 * particular plan view"): every new drag is stored under the PLAN VIEW it was made in — the
 * view record's id (its sheets and PDF print it), or the level's untyped live plan.
 */
export const viewTagScopeKey = (viewId, levelId) => (viewId ? `view:${viewId}` : levelId ? `live:${levelId}` : null)

export function getTagOffsets(model, scope) {
  if (!model || !scope) return {}
  const all = maps(model).settings.get(SETTINGS_KEY)
  const s = all && typeof all === 'object' ? all[scope] : null
  return s && typeof s === 'object' ? s : {}
}

/** set (or with null, clear) one tag's offset in a scope — one undo step */
export function setTagOffset(model, scope, tagKey, offset) {
  if (!model || !scope || !tagKey) return
  const stored = maps(model).settings.get(SETTINGS_KEY)
  const all = stored && typeof stored === 'object' ? stored : {}
  const sc = { ...(all[scope] || {}) }
  if (offset && (Math.abs(offset[0]) > 1e-6 || Math.abs(offset[1]) > 1e-6)) sc[tagKey] = [offset[0], offset[1]]
  else delete sc[tagKey]
  const next = { ...all }
  if (Object.keys(sc).length) next[scope] = sc
  else delete next[scope]
  transact(model, () => { maps(model).settings.set(SETTINGS_KEY, next) })
}

/**
 * TAG ORIGINS (2026-10-15, Matt: "a toggle that can turn on original locations so users can see
 * everything that moved in one view and potentially reset things"). Nothing new is stored: every
 * hand placement is an OFFSET from where the layout puts the tag, so the original spot is the
 * drawn spot minus the tag's total offset in this plan view. That total has two parts, and this
 * is the one place that knows which older store each family actually draws from:
 *   base — the shared placement older than per-view drags: a room label on its own level reads
 *          RoomRecord.labelOffset ONLY; a wall tag reads wallTagOverrides; every other family
 *          (openings, through-room labels, equipment, RCP and assembly tags, demo keynotes) reads
 *          the level-wide tagOffsets scope
 *   view — this plan view's own drags (viewTagScopeKey), drawn on top of the base
 * `base` is also what a reset cancels (the view entry becomes −base), so a reset lands exactly on
 * the faint "original" copy.
 */
export function tagPlacementResolver(model, { levelId, planType, viewId }) {
  const view = getTagOffsets(model, viewTagScopeKey(viewId, levelId))
  const level = getTagOffsets(model, tagScopeKey(levelId, planType))
  const walls = model ? getWallTagOverrides(model) : {}
  let rooms = null
  const roomOf = (id) => {
    if (!rooms) rooms = new Map((model ? listRooms(model) : []).map((r) => [r.id, r]))
    return rooms.get(id)
  }
  const base = (key) => {
    if (key.startsWith('room:')) {
      const r = roomOf(key.slice(5))
      if (r && levelId && r.levelId === levelId) return r.labelOffset || null
      return level[key] || null
    }
    if (key.startsWith('wall:')) return walls[key.slice(5)]?.offset || null
    return level[key] || null
  }
  const total = (key) => {
    const b = base(key), v = view[key]
    if (!b && !v) return null
    const d = [(b?.[0] || 0) + (v?.[0] || 0), (b?.[1] || 0) + (v?.[1] || 0)]
    return Math.hypot(d[0], d[1]) > 1e-4 ? d : null
  }
  return { base, total }
}

/** RESET ALL (TAG ORIGINS): every listed tag back to its automatic spot in ONE view scope — one
 *  undo step. `baseOf(key)` = the resolver's base (the view entry cancels it; none = deleted).
 *  `unhide` (HAND-ADJUSTED DIMENSIONS): auto-dimension strings to show again, same step. */
export function resetTagsInScope(model, scope, keys, baseOf, unhide = null) {
  if (!model || !scope || (!keys?.length && !unhide?.length)) return
  keys = keys || []
  const stored = maps(model).settings.get(SETTINGS_KEY)
  const all = stored && typeof stored === 'object' ? stored : {}
  const sc = { ...(all[scope] || {}) }
  for (const k of keys) {
    const b = baseOf(k)
    if (b && (Math.abs(b[0]) > 1e-6 || Math.abs(b[1]) > 1e-6)) sc[k] = [-b[0], -b[1]]
    else delete sc[k]
  }
  const next = { ...all }
  if (Object.keys(sc).length) next[scope] = sc
  else delete next[scope]
  const hid = unhide?.length ? withHidden(model, scope, unhide, false) : null
  transact(model, () => {
    maps(model).settings.set(SETTINGS_KEY, next)
    if (hid) maps(model).settings.set(HIDDEN_KEY, hid)
  })
}

/**
 * HAND-ADJUSTED DIMENSIONS (2026-10-15): the automatic dimension strings a plan view hides —
 * settings `autoDimHidden`: { [view scope]: { 'dimstr:<string>': true } }. Slides and number
 * nudges live in the tag offsets above under the same view scope (keys 'dimstr:…' / 'dimtxt:…'),
 * so drags, faint originals, resets and the log are the tag machinery's own.
 */
const HIDDEN_KEY = 'autoDimHidden'
export function getAutoDimHidden(model, scope) {
  if (!model || !scope) return {}
  const all = maps(model).settings.get(HIDDEN_KEY)
  const sc = all && typeof all === 'object' ? all[scope] : null
  return sc && typeof sc === 'object' ? sc : {}
}
function withHidden(model, scope, keys, hide) {
  const stored = maps(model).settings.get(HIDDEN_KEY)
  const all = stored && typeof stored === 'object' ? stored : {}
  const sc = { ...(all[scope] || {}) }
  for (const k of keys) { if (hide) sc[k] = true; else delete sc[k] }
  const next = { ...all }
  if (Object.keys(sc).length) next[scope] = sc
  else delete next[scope]
  return next
}
/** hide (or show again) one auto-dimension string in one view — one undo step */
export function setAutoDimHidden(model, scope, key, hide) {
  if (!model || !scope || !key) return
  const next = withHidden(model, scope, [key], hide)
  transact(model, () => { maps(model).settings.set(HIDDEN_KEY, next) })
}
/** everything autoDimensionPrims needs to draw one plan view's hand adjustments */
export function autoDimViewAdjust(model, viewId, levelId) {
  const scope = viewTagScopeKey(viewId, levelId)
  return { adjust: getTagOffsets(model, scope), hidden: getAutoDimHidden(model, scope) }
}

/** the family part of a tagKey (`open`, `room`, `wall`, `demo`, `anno.equiptag`, …) */
export const tagFamily = (key) => {
  const s = String(key || '')
  const i = s.indexOf(':')
  return i > 0 ? s.slice(0, i) : s
}

const add = (p, d) => [p[0] + d[0], p[1] + d[1]]

/**
 * one glyph moved by `d` (every geometric field the primitive vocabulary uses).
 *
 * LEADERED TAGS (ELEVATION + SECTION TAG PLACEMENT, 2026-10-20): a tag with a leader keeps
 * its leader's tip on the thing it names. A glyph marked `tagPin: 'all'` (the dot at the
 * tip) never moves; `tagPin: 'a'` (a leader leg starting at the tip) keeps its `a` end and
 * moves its `b` end, so the leg re-runs from the tip to wherever the label now sits.
 * `tagAxis` ([x, y] unit vector) limits the move to that direction — a dimension string
 * slides along its witnesses only, so they stay square.
 */
export function translatePrim(p, d) {
  if (p.tagLeader) return leaderLabelGlyph(p.tagLeader, d, p)
  if (p.tagPin === 'all') return p
  if (p.tagAxis) {
    const k = d[0] * p.tagAxis[0] + d[1] * p.tagAxis[1]
    d = [p.tagAxis[0] * k, p.tagAxis[1] * k]
  }
  const q = { ...p }
  if (p.at) q.at = add(p.at, d)
  if (p.c) q.c = add(p.c, d)
  if (p.a && p.tagPin !== 'a') q.a = add(p.a, d)
  if (p.b) q.b = add(p.b, d)
  if (p.pts) q.pts = p.pts.map((v) => add(v, d))
  return q
}

/**
 * SITE ITEM LABELS (2026-10-20, Matt: "text with leaders and one bend line. They should
 * automatically switch the side the leader comes out of based on where the user moves the text
 * relative to the arrow head"): one glyph of a leadered label, laid out from the arrowhead's tip
 * and the bend (`elbow`) moved by `d`. The tail and the text sit on whichever side of the tip
 * the bend now is — so a drag across the arrowhead swaps them, live and on every sheet / PDF,
 * since every placement path runs through translatePrim. `L.role`: 'arrow' (the arrowhead,
 * on the item's edge facing the bend; one per item, `L.i`) · 'leg' (edge to bend) · 'tail' (the short level line) · 'text'.
 * `d` = [0, 0] lays the label out where its builder put it — the builder uses this same function.
 */
export function leaderLabelGlyph(L, d, base = {}) {
  const e = [L.elbow[0] + (d?.[0] || 0), L.elbow[1] + (d?.[1] || 0)]
  // the side is read against the item (a group: the middle of its items)
  const sd = e[0] >= L.ref[0] ? 1 : -1
  // the moved bend rides on the glyph, so offsets compose (a view's drag on top of a stored one)
  const q = { ...base, tagLeader: { ...L, elbow: e } }
  if (L.role === 'arrow' || L.role === 'leg') {
    // ONE LEADER PER ITEM, the arrowhead on the item's edge that faces the note (2026-10-20,
    // Matt) — re-found from the moved bend, so it walks round the item as the note moves
    const t = L.targets[L.i || 0]
    const P = edgeToward(t.c, e, t.ring)
    if (L.role === 'leg') return { ...q, kind: 'line', a: P, b: e }
    const dx = e[0] - P[0], dy = e[1] - P[1]
    const len = Math.hypot(dx, dy) || 1
    const u = [dx / len, dy / len], aw = L.aw
    return { ...q, kind: 'poly', closed: true, filled: true, pts: [
      [P[0], P[1]],
      [P[0] + u[0] * aw - u[1] * aw * 0.28, P[1] + u[1] * aw + u[0] * aw * 0.28],
      [P[0] + u[0] * aw + u[1] * aw * 0.28, P[1] + u[1] * aw - u[0] * aw * 0.28],
    ] }
  }
  if (L.role === 'tail') return { ...q, kind: 'line', a: e, b: [e[0] + sd * L.tail, e[1]] }
  // text: just past the tail, centred on it
  const at = [e[0] + sd * (L.tail + L.gap), e[1] - L.th * 0.35]
  const out = { ...q, kind: 'text', at, text: L.text, hFt: L.th }
  if (sd > 0) delete out.anchor
  else out.anchor = 'end'
  return out
}

/** the left edge of a leadered label's text, moved by `d` (left-justified alignment snaps) */
export function leaderLabelLeft(L, d) {
  const e0 = L.elbow[0] + (d?.[0] || 0)
  return e0 >= L.ref[0] ? e0 + L.tail + L.gap : e0 - L.tail - L.gap - L.w
}

/** stamp one tag's glyphs with its key (and optional leader pins already on them) */
export function keyTag(prims, tagKey) {
  return prims.map((p) => ({ ...p, tagKey }))
}

/**
 * Apply a scope's stored offsets to a prim list. Families that already placed their own
 * offset (so their collision layout accounts for it) mark their glyphs `tagOffsetApplied`.
 */
export function applyTagOffsets(prims, model, scope, opts = {}) {
  if (!prims?.length) return prims
  const offs = getTagOffsets(model, scope)
  if (!Object.keys(offs).length) return prims
  let changed = false
  const out = prims.map((p) => {
    // `opts.view`: a view's own placement rides ON TOP of wherever the tag already is (layout +
    // any older placement), so it applies to every keyed glyph
    // auto-dimension numbers (`dimKey`) take their view nudge inside autoDimensionPrims
    const d = p.tagKey && !p.dimKey && (opts.view || !p.tagOffsetApplied) ? offs[p.tagKey] : null
    if (!d) return p
    changed = true
    return translatePrim(p, d)
  })
  return changed ? out : prims
}

/** approximate text extent (the tag families' own CHAR_W convention) */
const CHAR_W = 0.62

/**
 * Hit boxes for the movable tags in a prim list: { tagKey, minX, minY, maxX, maxY, prims }.
 * Rotated text (wall tags) boxes its rotated rectangle's bounds.
 */
export function tagHitBoxes(prims) {
  const by = new Map()
  const grow = (k, p, x, y) => {
    let b = by.get(k)
    if (!b) { b = { tagKey: k, minX: Infinity, minY: Infinity, maxX: -Infinity, maxY: -Infinity, prims: [] }; by.set(k, b) }
    if (x < b.minX) b.minX = x; if (x > b.maxX) b.maxX = x
    if (y < b.minY) b.minY = y; if (y > b.maxY) b.maxY = y
    if (p && !b.prims.includes(p)) b.prims.push(p)
  }
  for (const p of prims || []) {
    const k = p.tagKey
    // a leader leg or its tip dot is not where the tag is grabbed (it stays on its element)
    if (!k || p.tagPin) continue
    if (p.kind === 'text' && p.at) {
      const h = p.hFt || 0.5
      const w = String(p.text || '').length * CHAR_W * h
      const x0 = p.anchor === 'middle' ? -w / 2 : p.anchor === 'end' ? -w : 0
      const corners = [[x0, -h * 0.25], [x0 + w, -h * 0.25], [x0 + w, h * 1.05], [x0, h * 1.05]]
      const r = p.rot || 0, cs = Math.cos(r), sn = Math.sin(r)
      for (const [x, y] of corners) grow(k, p, p.at[0] + x * cs - y * sn, p.at[1] + x * sn + y * cs)
    } else if (p.kind === 'circle' && p.c) {
      grow(k, p, p.c[0] - p.r, p.c[1] - p.r); grow(k, p, p.c[0] + p.r, p.c[1] + p.r)
    } else if (p.pts) {
      for (const v of p.pts) grow(k, p, v[0], v[1])
    } else if (p.a && p.b) {
      grow(k, p, p.a[0], p.a[1]); grow(k, p, p.b[0], p.b[1])
    }
  }
  return [...by.values()].filter((b) => Number.isFinite(b.minX))
}
